import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';

import SuggestedView from '@/util/SuggestedView';
import ShareIcon from '@/components/ShareIcon';
import { starClicked } from '@/actions';
import {
  getAlertsDisabled,
  getExportsDisabled,
  getFeedsDisabled,
  getUserActions,
  getViews,
} from '@/store-selectors';
import { Button, Tooltip, TrevorIcon } from '@trevor/ui-components';
import { QueryName, SaveButton } from '@/views/QueryBuilder/subcomponents';
import styled from 'styled-components';
import { safeOnClick } from '@/util/helpers/event-helpers';
import { useEventAwareModal, useModal } from '@/features/modals/modalSlice';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { useGetSavedQuery } from '@/features/query-builder/savedQueriesApiSlice';
import {
  useDataPresentationParam,
  useDatasourceId,
  useSeriesId,
  useTimeoutToggle,
} from '@/hooks';
import { useDownloadAsCSV } from '@/hooks/useDownloadAsCSV';
import { useLoadRows_LEGACY } from '@/features/query-builder/hooks/LEGACY';
import { ShareModal } from '@/features/share-modal';
import { SHARE_OPTION } from '@/features/share-modal/ShareModal.constants';
import { userIsFinishedOnboarding } from '@/components/HighlightFeature';
import { useHasSavedQueryChanged } from '@/views/QueryBuilder/hooks/useHasSavedQueryChanged';
import {
  OPEN_EMAIL_ALERT_MODAL,
  OPEN_GOOGLE_SHEETS_EXPORT_MODAL,
  OPEN_SHARE_WITH_TEAM_MODAL,
  OPEN_SLACK_NOTIFICATION_MODAL,
} from '@/constants/custom-event-names';
import { selectChartConfigurationHasChanged } from '@/features/query-builder/queryBuilderSlice';
import { ModalUpdateSavedQuery } from './ModalUpdateSavedQuery';

const DiscardChangesButton = () => {
  const navigate = useNavigate();
  const datasourceId = useDatasourceId();
  const dataPresentation = useDataPresentationParam();
  const { data: selectedView } = useGetSavedQuery();
  const savedQueryHasChanged = useHasSavedQueryChanged();
  const chartConfigHasChanged = useAppSelector(
    selectChartConfigurationHasChanged,
  );

  if (!savedQueryHasChanged) return null;

  const handleDiscardChanges = () => {
    const dataPresentationPath = dataPresentation ? `/${dataPresentation}` : '';
    const nextUrl = `/datasources/${datasourceId}/saved-queries/${selectedView.seriesId}${dataPresentationPath}`;
    navigate(nextUrl);
    if (chartConfigHasChanged) {
      navigate(0);
    }
  };

  return (
    <Button size="small" variant="secondary" onClick={handleDiscardChanges}>
      Discard changes
    </Button>
  );
};

const ShareWithTeamButton = () => {
  const { queryId } = useLoadRows_LEGACY();
  const seriesId = useSeriesId();
  const [isNotificationShown, showNotification] = useTimeoutToggle(2500);
  const [isModalOpen] = useEventAwareModal(OPEN_SHARE_WITH_TEAM_MODAL);

  const handleCopyUrl = () => {
    window.navigator.clipboard
      .writeText(
        `${window.location.origin}/share/${seriesId ? 'v' : 'r'}/${
          seriesId || queryId
        }`,
      )
      .then(showNotification)
      .catch(() => window.alert('Oops, unable to copy'));
  };

  return (
    <>
      <div className="position-relative">
        <ShareIcon
          key={isNotificationShown ? 'copied!' : 'Share link with team'}
          id="shareCopyLink"
          tooltip={isNotificationShown ? 'Copied!' : 'Share link with team'}
          onClick={handleCopyUrl}
        >
          <TrevorIcon name="link" />
        </ShareIcon>
      </div>
      {isModalOpen && <ShareModal initialShareOption={SHARE_OPTION.link} />}
    </>
  );
};

const DownloadAsCSVButton = () => {
  const handleDownLoad = useDownloadAsCSV();

  return (
    <>
      <div data-tooltip-id="download">
        <Button
          variant="secondary"
          size="small"
          onClick={handleDownLoad}
          icon={<TrevorIcon name="download" />}
        />
      </div>
      <Tooltip id="download" place="bottom">
        Download as CSV
      </Tooltip>
    </>
  );
};

const ExportToGoogleSheetsButton = () => {
  const { dataTable } = useLoadRows_LEGACY();
  const seriesId = useSeriesId();
  const [isModalOpen, handleModalOpen] = useEventAwareModal(
    OPEN_GOOGLE_SHEETS_EXPORT_MODAL,
  );
  const areExportsDisabled = useSelector(getExportsDisabled);
  const areFeedsDisabled = useSelector(getFeedsDisabled);

  const disabled =
    areExportsDisabled || areFeedsDisabled || !seriesId || !dataTable;

  return (
    <>
      <ShareIcon
        id="sheets"
        tooltip="Export to Google Sheets"
        onClick={handleModalOpen}
        disabled={disabled}
        disabledTip={
          areExportsDisabled || areFeedsDisabled ? 'disabled by admin' : null
        }
      >
        <TrevorIcon name="google-sheets" />
      </ShareIcon>
      {isModalOpen && (
        <ShareModal initialShareOption={SHARE_OPTION.googleSheets} />
      )}
    </>
  );
};

export const CreateSlackAlertButton = () => {
  const seriesId = useSeriesId();
  const { dataTable } = useLoadRows_LEGACY();
  const [isModalOpen, handleModalOpen] = useEventAwareModal(
    OPEN_SLACK_NOTIFICATION_MODAL,
  );
  const areAlertsDisabled = useSelector(getAlertsDisabled);

  const disabled = areAlertsDisabled || !seriesId || !dataTable;

  return (
    <>
      <ShareIcon
        id="slack"
        tooltip="Create Slack alert"
        onClick={handleModalOpen}
        disabled={disabled}
        disabledTip={areAlertsDisabled ? 'disabled by admin' : null}
      >
        <TrevorIcon name="slack" />
      </ShareIcon>
      {isModalOpen && <ShareModal initialShareOption={SHARE_OPTION.slack} />}
    </>
  );
};

const EmailAlertModal = () => {
  const [isModalOpen] = useEventAwareModal(OPEN_EMAIL_ALERT_MODAL);

  return isModalOpen ? (
    <ShareModal initialShareOption={SHARE_OPTION.email} />
  ) : null;
};

const TopControlsContainer = styled.div`
  padding: 10px 20px;
  display: flex;
  justify-content: space-between;
  border-bottom: 1px solid #e8e8e8; // TODO: this is a old color
  height: 60px;

  > div:first-child {
    width: 40%;
    display: flex;
    align-items: center;
  }

  > div:last-child {
    width: 50%;
    display: flex;
    justify-content: end;
  }
`;

const FlexContainer = styled.div`
  width: 100%;
  display: inline-flex;
  gap: var(--spacing-s);
`;

const ShareOptionsButton = () => {
  const [isModalOpen, handleModalOpen] = useModal();
  return (
    <>
      <ShareIcon
        id="all"
        tooltip="More share / export options"
        onClick={handleModalOpen}
      >
        <TrevorIcon name="menu-horizontal" />
      </ShareIcon>
      {isModalOpen && <ShareModal />}
    </>
  );
};

const ExportAsSQLButton = () => {
  const [isModalOpen, handleModalOpen] = useModal();
  return (
    <>
      <ShareIcon id="sql" tooltip="Export as SQL" onClick={handleModalOpen}>
        <TrevorIcon name="sql" />
      </ShareIcon>
      {isModalOpen && <ShareModal initialShareOption={SHARE_OPTION.sql} />}
    </>
  );
};

export const TopControls = () => {
  const dispatch = useAppDispatch();
  const { data: selectedView } = useGetSavedQuery();
  const views = useSelector(getViews);
  const userActions = useSelector(getUserActions);
  const [isUpdateSavedQueryModalOpen, handleUpdateSavedQueryModal] = useModal();

  const handleStartClick = (view: typeof selectedView, on: boolean) => () => {
    dispatch(starClicked(view, on));
  };

  // viewSeries and isFavourite are required to star saved queries
  const viewSeries = !selectedView
    ? null
    : !(selectedView instanceof SuggestedView)
    ? selectedView.seriesId
    : null;

  const isFavourite = !selectedView
    ? null
    : !(selectedView instanceof SuggestedView) &&
      // @ts-expect-error: TODO: add proper typing.
      views.favourites.find((series) => series === viewSeries);

  return (
    <TopControlsContainer>
      {userIsFinishedOnboarding(userActions) && (
        <>
          <FlexContainer>
            <DiscardChangesButton />
            {selectedView && (
              <>
                <QueryName
                  viewBeingEdited={selectedView}
                  onRename={handleUpdateSavedQueryModal}
                />
                <div>
                  <TrevorIcon
                    name="pen"
                    data-tooltip-id="edit"
                    onClick={handleUpdateSavedQueryModal}
                    style={{ color: '#474752', cursor: 'pointer' }}
                  />
                  <Tooltip id="edit" place="bottom">
                    Rename query
                  </Tooltip>
                  {isUpdateSavedQueryModalOpen && <ModalUpdateSavedQuery />}
                </div>
                <div>
                  <TrevorIcon
                    name={isFavourite ? 'star-filled' : 'star'}
                    data-tooltip-id="favourite"
                    onClick={safeOnClick(
                      handleStartClick(selectedView, !isFavourite),
                    )}
                    style={{ color: '#474752', cursor: 'pointer' }}
                  />
                  <Tooltip id="favourite" place="bottom">
                    Star query
                  </Tooltip>
                </div>
              </>
            )}
          </FlexContainer>
          <FlexContainer>
            <ShareWithTeamButton />
            <DownloadAsCSVButton />
            <ExportToGoogleSheetsButton />
            <CreateSlackAlertButton />
            <ExportAsSQLButton />
            <ShareOptionsButton />
            <SaveButton />
            <EmailAlertModal />
          </FlexContainer>
        </>
      )}
    </TopControlsContainer>
  );
};
